import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/icons';
import { QuestionRow } from '../../components/QuestionRow';
import { ORDER_HINT } from '../../model';
import { manualHasRepeats, manualIsValid, MAX_ITEMS } from '../../wizardStore';
import type { WizardDraft, WizardNewItem } from '../../wizardStore';
import { questionTypeLabel } from '../../words';
import QuestionImageField from '../../../components/QuestionImageField';

interface Props {
  draft: WizardDraft;
  movedIndex: number | null;
  onMove: (index: number, delta: number) => void;
  onRemove: (index: number) => void;
  onAddManual: (item: WizardNewItem) => void;
  onBack: () => void;
  onNext: () => void;
}

/** Шаг 2: три способа взять вопросы и общий список «Вопросы в этом тесте» (макет v6). */
export const StepQuestions: React.FC<Props> = ({ draft, movedIndex, onMove, onRemove, onAddManual, onBack, onNext }) => {
  const navigate = useNavigate();
  const [panel, setPanel] = useState<'manual' | 'file' | null>(null);
  const [text, setText] = useState('');
  const [answers, setAnswers] = useState<string[]>(['', '', '']);
  const [right, setRight] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [answerImages, setAnswerImages] = useState<string[]>(['', '', '']);
  const [answerUploads, setAnswerUploads] = useState<number[]>([]);
  const count = draft.items.length;
  const full = count >= MAX_ITEMS;
  const valid = manualIsValid(text, answers, right, answerImages);
  const repeats = manualHasRepeats(answers, answerImages);
  const imageWithoutText = answerImages.some((url, index) => url.trim() !== '' && !answers[index].trim());
  const uploading = imageUploading || answerUploads.length > 0;
  const setAnswerUploading = (index: number, busy: boolean) => {
    setAnswerUploads((previous) => busy
      ? (previous.includes(index) ? previous : [...previous, index])
      : previous.filter((value) => value !== index));
  };

  const addManual = () => {
    if (!valid || full || uploading) return;
    onAddManual({ kind: 'new', text: text.trim(), answers, rightIndex: right,
      ...(imageUrl.trim() ? { image_url: imageUrl.trim() } : {}),
      ...(answerImages.some((url) => url.trim()) ? { answer_image_urls: answerImages.map((url) => url.trim()) } : {}) });
    setPanel(null);
    setText('');
    setAnswers(['', '', '']);
    setRight(0);
    setImageUrl('');
    setAnswerImages(['', '', '']);
  };

  return (
    <>
      <div className="tw-card tw-stack-sm">
        <h2>Откуда возьмём вопросы?</h2>
        <p className="tw-help">Можно пользоваться несколькими способами по очереди — все вопросы соберутся в один список ниже.</p>
        <div className="tw-tiles">
          <button type="button" className="tw-tile tw-tile-strong" onClick={() => navigate('/admin/tests/new/questions/add')}>
            <span className="tw-tile-ico"><Icon name="book" size={28} /></span>
            <span className="tw-tag">Проще всего</span>
            <span className="tw-tile-title">Взять готовые вопросы</span>
            <span className="tw-tile-text">Выберите из вопросов, которые уже есть в системе.</span>
          </button>
          <button type="button" className="tw-tile" aria-expanded={panel === 'manual'} onClick={() => setPanel(panel === 'manual' ? null : 'manual')}>
            <span className="tw-tile-ico"><Icon name="pencil" size={28} /></span>
            <span className="tw-tile-title">Написать самому</span>
            <span className="tw-tile-text">Придумайте вопрос и варианты ответа.</span>
          </button>
          <button type="button" className="tw-tile" aria-expanded={panel === 'file'} onClick={() => setPanel(panel === 'file' ? null : 'file')}>
            <span className="tw-tile-ico"><Icon name="upload" size={28} /></span>
            <span className="tw-tile-title">Загрузить из файла</span>
            <span className="tw-tile-text">Если вопросы уже записаны в таблице Excel.</span>
          </button>
        </div>

        {panel === 'manual' && (
          <div className="tw-panel tw-stack-sm">
            <h3>Новый вопрос</h3>
            <div>
              <label className="tw-label" htmlFor="tw-d-q">Текст вопроса</label>
              <input
                id="tw-d-q"
                className="tw-field"
                type="text"
                maxLength={5000}
                placeholder="Например: Для чего нужен тормозной диск?"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </div>
            <QuestionImageField
              id="tw-d-image"
              value={imageUrl}
              onChange={setImageUrl}
              onUploadingChange={setImageUploading}
              appearance="workspace"
            />
            <div>
              <div className="tw-label">Варианты ответа. Отметьте кружком правильный.</div>
              <div className="tw-stack-sm">
                {answers.map((value, index) => (
                  <div key={index}>
                  <div className="tw-answer-row">
                    {/* Кружок — 20 px, как отметки выбора в проекте; подпись вокруг него увеличивает область нажатия до 44 px. */}
                    <label className="tw-radio-target">
                      <input
                        className="tw-radio"
                        type="radio"
                        name="right-answer"
                        aria-label={`Правильный ответ — вариант ${index + 1}`}
                        checked={right === index}
                        onChange={() => setRight(index)}
                      />
                    </label>
                    <input
                      className="tw-field"
                      type="text"
                      aria-label={`Вариант ответа ${index + 1}`}
                      maxLength={2000}
                      placeholder={`Вариант ${index + 1}`}
                      value={value}
                      onChange={(event) => setAnswers(answers.map((old, position) => (position === index ? event.target.value : old)))}
                    />
                  </div>
                  <div style={{ marginLeft: 32 }}>
                    <QuestionImageField
                      id={`tw-d-answer-image-${index}`}
                      label={`Фото варианта ответа ${index + 1} (необязательно)`}
                      imageAlt={`Вариант ответа ${index + 1}`}
                      fileLabel={`Файл изображения варианта ответа ${index + 1}`}
                      value={answerImages[index] || ''}
                      onChange={(url) => setAnswerImages((previous) => previous.map((old, position) => position === index ? url : old))}
                      onUploadingChange={(busy) => setAnswerUploading(index, busy)}
                      appearance="workspace"
                      compact
                    />
                  </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="tw-row tw-btn-row">
              <button type="button" className="tw-btn tw-btn-primary" disabled={!valid || full || uploading} onClick={addManual}>
                Добавить вопрос в тест
              </button>
              <button type="button" className="tw-btn" onClick={() => setPanel(null)}>Отмена</button>
            </div>
            {full && <p className="tw-help">{`В тесте уже ${MAX_ITEMS} вопросов: больше добавить нельзя.`}</p>}
            {!full && repeats && <p className="tw-help">Варианты ответа не должны повторяться.</p>}
            {!full && !repeats && imageWithoutText && <p className="tw-help">Добавьте текст к варианту с фото.</p>}
            {!full && !repeats && !imageWithoutText && !valid && (
              <p className="tw-help">Впишите вопрос и хотя бы два варианта ответа, затем отметьте правильный.</p>
            )}
          </div>
        )}

        {panel === 'file' && (
          <div className="tw-panel tw-stack-sm">
            <h3>Загрузка вопросов из таблицы Excel</h3>
            <ol className="tw-olist">
              <li>Скачайте образец таблицы — в нём уже есть пример.</li>
              <li>Впишите свои вопросы в эту таблицу и сохраните файл.</li>
              <li>Нажмите «Выбрать файл» и найдите сохранённую таблицу.</li>
            </ol>
            <div className="tw-row tw-btn-row">
              <button type="button" className="tw-btn" disabled>
                <Icon name="file" />
                Скачать образец
              </button>
              <button type="button" className="tw-btn" disabled>
                <Icon name="upload" />
                Выбрать файл
              </button>
              <button type="button" className="tw-btn" onClick={() => setPanel(null)}>Закрыть</button>
            </div>
            <p className="tw-help">
              Загрузка из таблицы пока недоступна на этом экране. Сейчас её можно сделать в разделе «Массовые назначения»
              {' ('}
              <a href="/admin/assignments" target="_blank" rel="noreferrer">открыть в новой вкладке</a>
              {'). Загруженные там вопросы потом можно взять здесь как готовые.'}
            </p>
          </div>
        )}
      </div>

      <div className="tw-card tw-stack-sm">
        <h2>{`Вопросы в этом тесте: ${count}`}</h2>
        {count === 0 && (
          <p className="tw-help">Пока нет ни одного вопроса. Выберите способ выше — например, «Взять готовые вопросы».</p>
        )}
        {count >= 2 && <p className="tw-help">{ORDER_HINT}</p>}
        <ul className="tw-qlist">
          {draft.items.map((item, index) => (
            // Новые вопросы не имеют номера, поэтому ключ — их место в списке; список меняется только через эти кнопки.
            <li key={`${item.kind}-${item.kind === 'source' ? item.id : index}`}>
              <QuestionRow
                number={index + 1}
                text={item.text}
                meta={item.kind === 'source'
                  ? questionTypeLabel(item.type)
                  : `${questionTypeLabel('single_choice')} · Написан вами`}
                moved={movedIndex === index}
                canUp={index > 0}
                canDown={index < count - 1}
                onUp={() => onMove(index, -1)}
                onDown={() => onMove(index, 1)}
                onRemove={() => onRemove(index)}
                removeLabel="Убрать из теста"
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="tw-foot">
        <button type="button" className="tw-btn" onClick={onBack}>
          <Icon name="left" />
          Назад
        </button>
        <button type="button" className="tw-btn tw-btn-primary tw-btn-big" disabled={count === 0} data-guide="wizard-questions-next" onClick={onNext}>
          Дальше: проверить
          <Icon name="right" />
        </button>
        {count === 0 && <span className="tw-help">Добавьте хотя бы один вопрос, чтобы продолжить.</span>}
      </div>
    </>
  );
};
